/**
 * MorePage — пятый слот навигации: всё, что не поместилось в четыре первых.
 *
 * ЗАЧЕМ ОН ЕСТЬ. У атлета 120+ экранов, у панели — пять слотов. Иерархия из
 * пяти пунктов сотню назначений не адресует, и до 2026-07-28 длинный хвост
 * был достижим только по цепочке из конкретной карточки: исчезло звено —
 * экран выпал из приложения молча. Так и потерялись «Устройства»,
 * «Беременность», протоколы жары и высоты (см. scripts/check_route_reachability.py).
 *
 * УСТРОЙСТВО. Сверху — строка, ведущая в поиск по всему приложению
 * (экраны + 27 линз + база знаний). Ниже — тематические группы плитками.
 * Порядок намеренный: кто знает слово — печатает, кто не знает — листает.
 * Поиск первым, потому что он масштабируется на любое число экранов,
 * а плитки — нет.
 *
 * Раскладка разделов — docs/_active/MOBILE_NAV_SPEC.md раздел 5.
 */
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  MagnifyingGlass,
  Barbell,
  Backpack,
  BookOpen,
  UsersThree,
  UserCircle,
  Gear,
  Info,
  CaretRight,
  type Icon as PhosphorIcon,
} from "@phosphor-icons/react";

import { useTgBackButton } from "../useTelegramWebApp";
import { goBackOrTo } from "../navUtils";

interface Tile {
  to: string;
  /** Ключ внутри неймспейса `nav`. */
  labelKey: string;
  labelFallback: string;
  icon: PhosphorIcon;
  testId: string;
}

interface Group {
  /** Ключ заголовка группы внутри `nav`. */
  titleKey: string;
  titleFallback: string;
  tiles: Tile[];
}

const GROUPS: Group[] = [
  {
    // Тренировки — первой группой: конструктор был доступен ровно из одного
    // места, третьим пунктом всплывающего меню на экране Плана, и потому
    // его не находили. Инструмент, которым собирают тренировку руками,
    // обязан иметь вход в общем списке разделов, а не только контекстный.
    titleKey: "more.group_training",
    titleFallback: "Тренировки",
    tiles: [
      { to: "/workout/build", labelKey: "more.workout_build", labelFallback: "Конструктор тренировки", icon: Barbell, testId: "more-workout-build" },
      { to: "/plan/templates", labelKey: "more.plan_templates", labelFallback: "Шаблоны планов", icon: Barbell, testId: "more-plan-templates" },
      // «Тесты порогов» держались на ЕДИНСТВЕННОЙ ссылке — из онбординг-хаба.
      // Пройдя онбординг, атлет терял их навсегда: ни в навигации, ни здесь,
      // ни с «Формы» или «Зон» входа не было. Guard сирот молчал, потому что
      // формально одна ссылка есть. При этом функция платная и числится в
      // реестре доступа (`threshold_tests`) — гейт есть, двери нет.
      { to: "/threshold-test", labelKey: "more.threshold_tests", labelFallback: "Тесты порогов", icon: Barbell, testId: "more-threshold-tests" },
    ],
  },
  {
    titleKey: "more.group_gear",
    titleFallback: "Экипировка",
    tiles: [
      { to: "/equipment", labelKey: "more.equipment", labelFallback: "Моя экипировка", icon: Backpack, testId: "more-equipment" },
      { to: "/gear", labelKey: "more.gear", labelFallback: "Снаряжение", icon: Backpack, testId: "more-gear" },
      { to: "/shoes", labelKey: "more.shoes", labelFallback: "Обувь", icon: Backpack, testId: "more-shoes" },
      { to: "/devices", labelKey: "more.devices", labelFallback: "Устройства", icon: Gear, testId: "more-devices" },
    ],
  },
  {
    titleKey: "more.group_knowledge",
    titleFallback: "Знания",
    tiles: [
      { to: "/knowledge", labelKey: "more.knowledge", labelFallback: "База знаний", icon: BookOpen, testId: "more-knowledge" },
      { to: "/exercises", labelKey: "more.exercises", labelFallback: "Упражнения", icon: BookOpen, testId: "more-exercises" },
    ],
  },
  {
    titleKey: "more.group_people",
    titleFallback: "Люди",
    tiles: [
      { to: "/sparring", labelKey: "more.sparring", labelFallback: "Спарринг", icon: UsersThree, testId: "more-sparring" },
      { to: "/friends", labelKey: "more.friends", labelFallback: "Друзья", icon: UsersThree, testId: "more-friends" },
      { to: "/challenges", labelKey: "more.challenges", labelFallback: "Челленджи", icon: UsersThree, testId: "more-challenges" },
      { to: "/refer", labelKey: "more.refer", labelFallback: "Пригласить друга", icon: UsersThree, testId: "more-refer" },
    ],
  },
  {
    titleKey: "more.group_me",
    titleFallback: "Я",
    tiles: [
      { to: "/profile", labelKey: "more.profile", labelFallback: "Профиль", icon: UserCircle, testId: "more-profile" },
      { to: "/questionnaire", labelKey: "more.questionnaire", labelFallback: "Анкета", icon: UserCircle, testId: "more-questionnaire" },
      { to: "/avatar", labelKey: "more.avatar", labelFallback: "Аватар", icon: UserCircle, testId: "more-avatar" },
      { to: "/privacy-dashboard", labelKey: "more.privacy", labelFallback: "Приватность", icon: UserCircle, testId: "more-privacy" },
    ],
  },
  {
    titleKey: "more.group_settings",
    titleFallback: "Настройки",
    tiles: [
      { to: "/settings", labelKey: "more.settings", labelFallback: "Все настройки", icon: Gear, testId: "more-settings" },
      { to: "/settings/integrations", labelKey: "more.integrations", labelFallback: "Интеграции", icon: Gear, testId: "more-integrations" },
      { to: "/settings/notifications", labelKey: "more.notifications", labelFallback: "Уведомления", icon: Gear, testId: "more-notifications" },
      { to: "/settings/language", labelKey: "more.language", labelFallback: "Язык", icon: Gear, testId: "more-language" },
    ],
  },
  {
    titleKey: "more.group_app",
    titleFallback: "Приложение",
    tiles: [
      { to: "/inbox", labelKey: "more.inbox", labelFallback: "Входящие", icon: Info, testId: "more-inbox" },
      { to: "/whats-new", labelKey: "more.whats_new", labelFallback: "Что нового", icon: Info, testId: "more-whats-new" },
      { to: "/feedback", labelKey: "more.feedback", labelFallback: "Обратная связь", icon: Info, testId: "more-feedback" },
    ],
  },
];

export function MorePage() {
  const { t } = useTranslation("nav");
  const nav = useNavigate();
  useTgBackButton(() => goBackOrTo(nav, "/"));

  return (
    <div className="p-3 space-y-5" data-testid="more-page">
      {/* Поиск первым: он единственный масштабируется на 120+ экранов.
          Не поле ввода, а кнопка — печатать удобнее на самом экране поиска,
          где есть автофокус и живая фильтрация. */}
      <button
        type="button"
        onClick={() => nav("/search")}
        data-testid="more-search-cta"
        className="indigo-card w-full flex items-center gap-2.5 px-3 py-3 hover:bg-bg-raised transition-colors text-left"
      >
        <MagnifyingGlass className="w-4 h-4 shrink-0 text-fg-muted" aria-hidden />
        <span className="text-sm text-fg-muted flex-1">
          {t("more.search_placeholder", { defaultValue: "Экраны, линзы, база знаний…" })}
        </span>
        <CaretRight className="w-3.5 h-3.5 text-fg-muted shrink-0" aria-hidden />
      </button>

      {GROUPS.map((group) => (
        <section key={group.titleKey} data-testid={`more-group-${group.titleKey}`}>
          <h2 className="text-[10px] font-mono uppercase tracking-[0.14em] text-fg-muted mb-2 px-0.5">
            {t(group.titleKey, { defaultValue: group.titleFallback })}
          </h2>
          <div className="grid grid-cols-2 gap-2">
            {group.tiles.map((tile) => {
              const Icon = tile.icon;
              return (
                <button
                  key={tile.to}
                  type="button"
                  data-testid={tile.testId}
                  onClick={() => nav(tile.to)}
                  className="indigo-card px-3 py-3 flex items-center gap-2.5 hover:bg-bg-raised transition-colors text-left"
                >
                  <Icon className="w-4 h-4 shrink-0 text-fg-muted" aria-hidden />
                  <span className="text-xs font-medium text-fg-secondary min-w-0 truncate">
                    {t(tile.labelKey, { defaultValue: tile.labelFallback })}
                  </span>
                </button>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
